import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

import { cn } from '../../lib/cn';

const FIELD_BASE =
  'w-full rounded-tight border bg-paper text-sm text-ink placeholder:text-ink-muted ' +
  'transition-colors duration-instant ease-standard ' +
  'focus:outline-none disabled:cursor-not-allowed disabled:bg-paper-soft disabled:text-ink-faint disabled:border-rule-soft';

function stateBorder(invalid: boolean | undefined): string {
  return invalid
    ? 'border-danger focus:border-danger'
    : 'border-rule focus:border-accent';
}

interface FieldWrapperProps {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean | undefined;
  children: ReactNode;
}

function FieldWrapper({ id, label, hint, error, required, children }: FieldWrapperProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={id} className="text-xs font-medium text-ink-soft">
          {label}
          {required ? <span className="ml-0.5 text-danger">*</span> : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, invalid, className, id, required, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const isInvalid = invalid || Boolean(error);

  return (
    <FieldWrapper id={fieldId} label={label} hint={hint} error={error} required={required}>
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={isInvalid || undefined}
        required={required}
        {...rest}
        className={cn(FIELD_BASE, stateBorder(isInvalid), 'h-8 px-2.5', className)}
      />
    </FieldWrapper>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, invalid, className, id, required, rows = 4, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const isInvalid = invalid || Boolean(error);

  return (
    <FieldWrapper id={fieldId} label={label} hint={hint} error={error} required={required}>
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={isInvalid || undefined}
        required={required}
        {...rest}
        className={cn(FIELD_BASE, stateBorder(isInvalid), 'px-2.5 py-2 leading-relaxed', className)}
      />
    </FieldWrapper>
  );
});
